// TextEditorTinyMC.jsx
import { useCallback, useRef, useState, useEffect, useLayoutEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { useTranslation } from "react-i18next";

const TextEditorTinyMC = ({
  data,
  isDummy,
  docUrl,
  isReadOnly = false,
  isArabic = false,
  onBlur = () => {},
}) => {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const lastEditorHeightRef = useRef(null);
  const [value, setValue] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [loading, setLoading] = useState(false);

  const updateEditorHeight = useCallback(() => {
    const container = containerRef.current;
    const editor = editorRef.current;
    if (!container || !editor) return;

    const parent = container.parentElement;
    if (!parent) return;

    const MIN_EDITOR_HEIGHT = 150; // Minimum allowed height
    const MIN_EDITOR_WIDTH = 520; // Minimum allowed width
    const parentStyles = window.getComputedStyle(parent);
    const verticalPadding =
      parseFloat(parentStyles.paddingTop) + parseFloat(parentStyles.paddingBottom);
    const availableHeight = parent.clientHeight - verticalPadding;
    const newEditorHeight = Math.max(MIN_EDITOR_HEIGHT, availableHeight);

    if (lastEditorHeightRef.current === newEditorHeight) return;
    lastEditorHeightRef.current = newEditorHeight;

    editor.editorContainer.style.height = `${newEditorHeight}px`;
    editor.editorContainer.style.minWidth = `${MIN_EDITOR_WIDTH}px`;
    editor.fire("ResizeEditor");
  }, []);

  const scheduleEditorResize = useCallback(() => {
    if (resizeFrameRef.current !== null) {
      cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      updateEditorHeight();
    });
  }, [updateEditorHeight]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const parent = container?.parentElement;
    if (!container || !parent) return;

    scheduleEditorResize();

    const resizeObserver = new ResizeObserver(scheduleEditorResize);
    resizeObserver.observe(parent);

    return () => {
      resizeObserver.disconnect();
      if (resizeFrameRef.current !== null) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [scheduleEditorResize]);

  // Load from docUrl or data.value
  useEffect(() => {
    if (docUrl) {
      setLoading(true);
      fetch(docUrl)
        .then((r) => r.text())
        .then((text) => {
          setValue(text);
          setInitialValue(text);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (data.value) {
      // support data.value directly
      setValue(data.value);
      setInitialValue(data.value);
    }
  }, [docUrl, data.value, isArabic]);

  // Set content when editor initializes
  const handleInit = (evt, editor) => {
    editorRef.current = editor;
    scheduleEditorResize();

    if (value) {
      editor.setContent(value);
    }
  };

  const handleReset = () => {
    const content = initialValue || "<p></p>";
    if (editorRef.current) editorRef.current.setContent(content);
    if (data.setValue)
      data.setValue(
        initialValue.replace(/<div[^>]*>/g, "").replace(/<\/div>$/, "")
      );
  };

  const topToolbar =
    "reset | cut copy custompaste | undo redo | searchreplace | bold italic underline strikethrough | subscript superscript | removeformat";
  const bottomToolbar =
    "blocks | table | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent";

  return (
    <div ref={containerRef} className="widget-container tw-h-full tw-min-h-0">
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js" // ← Points to public/tinymce/
        licenseKey="gpl" // ← Required for TinyMCE 8 self-hosted
        disabled={isReadOnly || loading}
        onInit={handleInit}
        onBlur={onBlur}
        value={isDummy ? data.value : value}
        onEditorChange={(content) => {
          setValue(content);
          if (data.setValue) {
            // clean the wrapper div before saving
            const cleanContent = content
              .replace(/^<div style="direction:[^"]*">/, "")
              .replace(/<\/div>$/, "");
            data.setValue(cleanContent);
          }
        }}
        init={{
          height: 355,
          menubar: false,
          directionality: "ltr",
          language: "en",
          // language_url: isArabic ? "/tinymce/langs/ar.js" : undefined, // for Arabic UI
          plugins:
            "table searchreplace lists link image code fullscreen directionality",
          block_formats:
            "Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;Heading 5=h5;Heading 6=h6",
          toolbar: [topToolbar, bottomToolbar],
          toolbar_mode: "wrap",
          branding: false,

          setup: (editor) => {
            editor.on("init", () => {
              editor.getContainer().id = "difr-tinymce-editor";
            });

            // CUSTOM RESET BUTTON
            editor.ui.registry.addButton("reset", {
              icon: "new-document",
              tooltip: t("ResetPopup.reset"),
              onAction: () => {
                editor.windowManager.open({
                  title: "",
                  body: {
                    type: "panel",
                    items: [
                      {
                        type: "htmlpanel",
                        html: `<p style="font-size: 16px; margin: 0; line-height: 22px !important;">${t(
                          "ResetPopup.Message"
                        )}</p>`,
                      },
                    ],
                  },
                  buttons: [
                    {
                      type: "submit",
                      text: t("ResetPopup.OK"),
                      buttonType: "primary",
                    },
                    {
                      type: "cancel",
                      text: t("ResetPopup.Cancel"),
                      buttonType: "secondary",
                    },
                  ],
                  onSubmit: (api) => {
                    handleReset();
                    api.close();
                  },
                });
              },
            });

            // CUSTOM PASTE BUTTON
            editor.ui.registry.addButton("custompaste", {
              icon: "paste",
              tooltip: "Paste",
              onAction: async () => {
                try {
                  const clipboardItems = await navigator.clipboard.read();
                  for (const item of clipboardItems) {
                    if (item.types.includes("text/html")) {
                      const blob = await item.getType("text/html");
                      const html = await blob.text();
                      editor.insertContent(html);
                      return;
                    }
                    if (item.types.includes("text/plain")) {
                      const blob = await item.getType("text/plain");
                      const text = await blob.text();
                      editor.insertContent(text);
                      return;
                    }
                  }
                } catch {
                  editor.execCommand("mcePasteFromClipboard");
                }
              },
            });
          },

          content_style: `
            body { direction: "ltr"; font-family: Arial; font-size: 12px; }
            p { font-size: 16px; }
            h1 { font-size: 28px; }
            h2 { font-size: 22px; }
            h3 { font-size: 18px; }
            h4 { font-size: 16px; }
            h5 { font-size: 14px; }
            h6 { font-size: 12px; }
          `,
        }}
      />
    </div>
  );
};

export default TextEditorTinyMC;
